import type React from 'react';
import { memo, useMemo } from 'react';
import type { ValueType } from 'react-select/lib/types';

import * as styles from './styles';

import { isVehicleDashcam } from '@cobliteam/api-lib';

import { useVehicle } from '@cobliteam/hooks';
import { useGetDevicesGroup } from '@cobliteam/hooks/src/useEntity/useDevices';

import {
  BodyText,
  DescriptionTitle,
  Divider,
  HintPopup,
  type ISelectOptionType,
  Label,
  Select,
  Tooltip,
  VehicleStepIcon,
} from '@cobliteam/base-components';

import DriveEtaIcon from '@material-ui/icons/DriveEta';
import HelpIcon from '@material-ui/icons/Help';

import useMediaStore from '../../../../store/zustand/useMediaStore';
import type { MediaRequestData } from '../../../../types/MediaRequest';
import { DEFAULT_MEDIA_REQUEST_DATA } from '../../../../utils/mediaUtils';

export const VehicleSelectionStep: React.FC = memo(() => {
  const mediaRequestData = useMediaStore((state) => state.requestMediaData);
  const setMediaRequestData = useMediaStore(
    (state) => state.setRequestMediaData
  );
  const { vehicleGroupId, vehicleId } = mediaRequestData as MediaRequestData;

  const { getVehicles } = useVehicle();
  const { data: vehicles = [], isFetching: isFetchingVehicles } = getVehicles({
    enabled: true,
    staleTime: 300000,
  });

  const dashcams = useMemo(
    () => vehicles.filter(isVehicleDashcam),

    [JSON.stringify(vehicles)]
  );

  const { data: vehicleGroups = [], isFetching: isFetchingVehicleGroups } =
    useGetDevicesGroup({ staleTime: 300000 });

  const vehicleGroupOptions = useMemo(() => {
    const groups = vehicleGroups
      .flatMap((group) => {
        const vehis = dashcams.filter(
          (vehi) => vehi?.id && group.vehicleIds?.includes(vehi.id)
        );

        return vehis.length
          ? {
              value: group.id,
              label: group.name,
              vehicles: vehis,
            }
          : [];
      })
      .sort((a, b) => {
        const groupA = a.label.toUpperCase();
        const groupB = b.label.toUpperCase();

        if (groupA < groupB) {
          return -1;
        }

        if (groupA > groupB) {
          return 1;
        }

        return 0;
      });
    const defaultGroup = { value: undefined, label: '- Todos -' };

    return [defaultGroup, ...groups];
  }, [JSON.stringify(vehicleGroups), JSON.stringify(dashcams)]);

  const vehicleOptions = useMemo(
    () =>
      dashcams
        .map((vehicle) => {
          const { brand, name } = vehicle.model;
          const { licensePlate } = vehicle;
          let label = '';

          if (!licensePlate) {
            label = vehicle.label;
          } else {
            label = licensePlate;

            if (brand) {
              label = `${licensePlate} | ${brand}`;

              if (name) {
                label = `${label} ${name}`;
              }
            }
          }

          return {
            value: vehicle.id,
            label,
            vehicle,
          };
        })
        .sort((a, b) => {
          const vehiA = a.label.toUpperCase();
          const vehiB = b.label.toUpperCase();

          if (vehiA < vehiB) {
            return -1;
          }

          if (vehiA > vehiB) {
            return 1;
          }

          return 0;
        }),

    [JSON.stringify(dashcams)]
  );

  const filteredvehicleOptions = useMemo(() => {
    if (vehicleGroupId) {
      const group = vehicleGroups.find((g) => g.id === vehicleGroupId);

      return vehicleOptions.filter((e) =>
        group?.vehicleIds?.some((g) => g === e.vehicle.id)
      );
    }
    return vehicleOptions;
  }, [
    JSON.stringify(vehicleOptions),
    vehicleGroupId,
    JSON.stringify(vehicleGroups),
  ]);

  const selectedVehicle = useMemo(
    () =>
      vehicleId
        ? filteredvehicleOptions.find((e) => e.value === vehicleId)
        : null,

    [vehicleId, JSON.stringify(filteredvehicleOptions)]
  );

  const selectedGroup = useMemo(
    () => vehicleGroupOptions.find((e) => e.value === vehicleGroupId),

    [vehicleGroupId, JSON.stringify(vehicleGroupOptions)]
  );

  const onChangeVehicle = (option: ValueType<ISelectOptionType>) => {
    const { value } = option as ISelectOptionType;

    setMediaRequestData({
      ...DEFAULT_MEDIA_REQUEST_DATA,
      vehicleId: value,
      vehicleGroupId: mediaRequestData?.vehicleGroupId,
    });
  };

  const onChangeGroup = (option: ValueType<ISelectOptionType>) => {
    const { value } = option as ISelectOptionType;

    setMediaRequestData({
      ...DEFAULT_MEDIA_REQUEST_DATA,
      vehicleGroupId: value,
    });
  };

  const formatVehicleGroupOptionLabel = (option: any) => {
    const { label } = option;

    if (option?.vehicles) {
      return (
        <div className={styles.groupTooltipCounter}>
          {label}
          <HintPopup title='Veículos com câmera' placement='top' arrow>
            <div>{`(${option.vehicles.length})`}</div>
          </HintPopup>
        </div>
      );
    }

    return label;
  };

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.leftSideWrapper}>
        <div className={styles.headerIconLine}>
          <DriveEtaIcon />
          <DescriptionTitle>Selecionar veículo</DescriptionTitle>
        </div>
        <Divider />
        <div className={styles.selectWrapper}>
          <div>
            <Label>Grupo</Label>
            <Tooltip placement='top-start' CustomIcon={HelpIcon} arrow>
              <BodyText>
                Abaixo estão listados apenas os
                <br />
                grupos que possuem veículos com o
                <br />
                dispositivo de câmera.
              </BodyText>
            </Tooltip>
          </div>
          <Select
            placeholder='Selecione um grupo'
            value={selectedGroup}
            options={vehicleGroupOptions}
            onChange={onChangeGroup}
            isLoading={isFetchingVehicles || isFetchingVehicleGroups}
            isDisabled={isFetchingVehicles || isFetchingVehicleGroups}
            formatOptionLabel={formatVehicleGroupOptionLabel}
          />
        </div>
        <div className={styles.selectWrapper}>
          <Label>Veículo *</Label>
          <Select
            placeholder='Selecione um veículo'
            value={selectedVehicle}
            options={filteredvehicleOptions}
            onChange={onChangeVehicle}
            isLoading={isFetchingVehicles || isFetchingVehicleGroups}
            isDisabled={isFetchingVehicles || isFetchingVehicleGroups}
          />
        </div>
      </div>
      <div className={styles.rightSideWrapper}>
        <VehicleStepIcon />
      </div>
    </div>
  );
});

export default VehicleSelectionStep;
